import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Building2, Truck, FileText, ArrowRight, ArrowLeft, CheckCircle2, SkipForward, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_BASE as API } from '../config';

const STEPS_SOLO = [
  { id: 'company', title: 'Add Your First Company', subtitle: 'Set up the fleet you manage claims for', icon: Truck, color: 'teal' },
  { id: 'claim', title: 'File Your First Claim', subtitle: 'Create a sample claim to see the workflow', icon: FileText, color: 'amber' },
  { id: 'done', title: 'You\'re All Set!', subtitle: 'Your workspace is ready', icon: Sparkles, color: 'teal' },
];

const STEPS_PROVIDER = [
  { id: 'org', title: 'Create an Organization', subtitle: 'Group your client companies under an org', icon: Building2, color: 'blue' },
  { id: 'company', title: 'Add a Company', subtitle: 'Add a fleet client to your organization', icon: Truck, color: 'teal' },
  { id: 'done', title: 'You\'re All Set!', subtitle: 'Your multi-org workspace is ready', icon: Sparkles, color: 'blue' },
];

export default function WelcomePage() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [companyName, setCompanyName] = useState('');
  const [orgName, setOrgName] = useState('');
  const [orgColor, setOrgColor] = useState('#2563eb');

  const isProvider = user?.account_type === 'provider';
  const steps = isProvider ? STEPS_PROVIDER : STEPS_SOLO;
  const current = steps[step];

  const colorClasses = {
    teal: { bg: 'bg-teal-500', hover: 'hover:bg-teal-600', light: 'bg-teal-50', iconBg: 'bg-teal-100', iconText: 'text-teal-600', dot: 'bg-teal-500', dotInactive: 'bg-teal-200' },
    blue: { bg: 'bg-blue-500', hover: 'hover:bg-blue-600', light: 'bg-blue-50', iconBg: 'bg-blue-100', iconText: 'text-blue-600', dot: 'bg-blue-500', dotInactive: 'bg-blue-200' },
    amber: { bg: 'bg-amber-500', hover: 'hover:bg-amber-600', light: 'bg-amber-50', iconBg: 'bg-amber-100', iconText: 'text-amber-600', dot: 'bg-amber-500', dotInactive: 'bg-amber-200' },
  };

  const markComplete = async () => {
    try {
      await axios.put(`${API}/auth/onboarding-status`, { onboarding_completed: true }, { withCredentials: true });
      await checkAuth();
    } catch (err) {
      // Non-blocking: user can still proceed even if the flag isn't persisted
      console.debug('onboarding-status persistence failed:', err?.message);
    }
  };

  const handleSkip = async () => {
    navigate('/');
  };

  const handleCreateOrg = async () => {
    if (!orgName.trim()) { toast.error('Enter an organization name'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/organizations`, { name: orgName.trim(), branding_color: orgColor }, { withCredentials: true });
      toast.success(`Organization "${orgName}" created!`);
      setStep(step + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create organization');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateCompany = async () => {
    if (!companyName.trim()) { toast.error('Enter a company name'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/companies`, { name: companyName.trim() }, { withCredentials: true });
      toast.success(`Company "${companyName}" added!`);
      setStep(step + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add company');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinish = async () => {
    await markComplete();
    navigate('/');
  };

  const cc = colorClasses[current.color];
  const Icon = current.icon;

  return (
    <div className="min-h-screen bg-gray-200 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-6">
          <img src="/logo.png" alt="Logo" className="w-12 h-12 mx-auto mb-2" />
          <h1 className="text-xl font-bold text-gray-900">Welcome, {user?.name?.split(' ')[0] || 'there'}!</h1>
          <p className="text-sm text-gray-500 mt-1">Let's set up your workspace in a few quick steps</p>
        </div>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 mb-5">
          {steps.map((s, i) => (
            <div key={s.id} className={`h-2 rounded-full transition-all duration-300 ${i === step ? `w-8 ${cc.dot}` : i < step ? `w-2 ${cc.dot}` : `w-2 ${cc.dotInactive}`}`} />
          ))}
        </div>

        <Card className="shadow-lg" data-testid="onboarding-card">
          <CardContent className="pt-6 pb-6">
            {/* Step header */}
            <div className="flex items-center gap-3 mb-5">
              <div className={`p-2.5 rounded-xl ${cc.iconBg}`}>
                <Icon className={`w-6 h-6 ${cc.iconText}`} />
              </div>
              <div>
                <h2 className="font-bold text-lg text-gray-900" data-testid="onboarding-step-title">{current.title}</h2>
                <p className="text-sm text-gray-500">{current.subtitle}</p>
              </div>
              <span className="ml-auto text-xs text-gray-400 font-medium">Step {step + 1}/{steps.length}</span>
            </div>

            {/* Step content */}
            <div className="animate-fadeSlideIn" key={current.id}>
              {/* Provider: Create Org */}
              {current.id === 'org' && (
                <div className="space-y-3">
                  <Input placeholder="Organization name (e.g. Southeast Claims LLC)" value={orgName} onChange={(e) => setOrgName(e.target.value)} className="h-11" data-testid="onboarding-org-name" />
                  <div className="flex items-center gap-3">
                    <label className="text-sm text-gray-600">Brand color:</label>
                    <input type="color" value={orgColor} onChange={(e) => setOrgColor(e.target.value)} className="w-8 h-8 rounded cursor-pointer border-0" data-testid="onboarding-org-color" />
                  </div>
                  <Button className={`w-full h-11 text-white ${cc.bg} ${cc.hover}`} onClick={handleCreateOrg} disabled={submitting} data-testid="onboarding-create-org-btn">
                    {submitting ? 'Creating...' : 'Create Organization'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}

              {/* Add Company */}
              {current.id === 'company' && (
                <div className="space-y-3">
                  <Input placeholder="Company name (e.g. Star Auto Transport Inc)" value={companyName} onChange={(e) => setCompanyName(e.target.value)} className="h-11" data-testid="onboarding-company-name" />
                  <Button className={`w-full h-11 text-white ${cc.bg} ${cc.hover}`} onClick={handleCreateCompany} disabled={submitting} data-testid="onboarding-create-company-btn">
                    {submitting ? 'Adding...' : 'Add Company'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}

              {/* File Claim (Solo only) */}
              {current.id === 'claim' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">We've set up your company. Now file your first claim to see the full workflow.</p>
                  <div className="flex gap-3">
                    <Button className={`flex-1 h-11 text-white ${cc.bg} ${cc.hover}`} onClick={() => { markComplete(); navigate('/intake'); }} data-testid="onboarding-file-claim-btn">
                      <FileText className="w-4 h-4 mr-2" />
                      Open Claim Form
                    </Button>
                    <Button variant="outline" className="h-11 px-5" onClick={() => setStep(step + 1)} data-testid="onboarding-skip-claim-btn">
                      Skip
                    </Button>
                  </div>
                </div>
              )}

              {/* Done */}
              {current.id === 'done' && (
                <div className="text-center space-y-4">
                  <div className={`w-16 h-16 rounded-full ${cc.iconBg} flex items-center justify-center mx-auto`}>
                    <CheckCircle2 className={`w-8 h-8 ${cc.iconText}`} />
                  </div>
                  <p className="text-sm text-gray-600">Your workspace is ready. Head to the dashboard to start managing claims.</p>
                  <Button className={`w-full h-11 text-white ${cc.bg} ${cc.hover}`} onClick={handleFinish} data-testid="onboarding-go-dashboard-btn">
                    Go to Dashboard
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}
            </div>

            {/* Footer: back + skip */}
            {current.id !== 'done' && (
              <div className="flex items-center justify-between mt-5 pt-4 border-t border-gray-100">
                {step > 0 ? (
                  <button type="button" onClick={() => setStep(step - 1)} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
                    <ArrowLeft className="w-3 h-3" /> Back
                  </button>
                ) : <span />}
                <button type="button" onClick={handleSkip} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1" data-testid="onboarding-skip-btn">
                  <SkipForward className="w-3 h-3" /> Skip for now
                </button>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center mt-4 text-xs text-gray-400">You can always do this later from Settings</p>
      </div>
    </div>
  );
}
